import { useState, useRef, useEffect, useCallback } from 'react';
import { Eye, EyeOff, Loader2, Mail, Lock, User, MailCheck, Phone } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { createRateLimiter } from '../../utils/rateLimiter';
import { validatePassword, PASSWORD_RULES, PASSWORD_MIN_LENGTH } from '../../utils/passwordValidation';
import { sanitizeError } from '../../utils/errorMessages';
import { INPUT_LIMITS } from '../../utils/inputValidation';
import { formatPhoneBR, normalizePhoneBR, isValidPhoneBR } from '../../utils/phone';
import { useTurnstile } from '../../hooks/useTurnstile';
import { useCopy, fill } from '../../i18n';
import analytics from '../../services/analyticsService';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

// SVG icons para OAuth (lucide não tem logos de marca)
const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" className="shrink-0">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

interface LoginScreenProps {
  redirectTo?: string;
  initialEmail?: string;
}

const LoginScreen = ({ redirectTo = '/', initialEmail }: LoginScreenProps) => {
  const { login, signUp, resetPassword, signInWithOAuth, loading, error, signUpSuccess, signUpEmail, clearSignUpSuccess } = useAuth();
  const copy = useCopy();
  const t = copy.auth;
  // As regras de senha devolvem chave, não texto: o rótulo vem do dicionário.
  const ruleLabel = (key: keyof typeof copy.auth.passwordRules) =>
    fill(copy.auth.passwordRules[key], { min: PASSWORD_MIN_LENGTH });

  // Verificar se é login de admin (não permite cadastro)
  const isAdminLogin = redirectTo.includes('/admin');

  // Ler modo e invite token da URL
  const urlParams = new URLSearchParams(window.location.search);
  const urlMode = urlParams.get('mode');
  const inviteToken = urlParams.get('invite');
  const [mode, setMode] = useState(
    urlMode === 'signup' ? 'signup' : urlMode === 'reset' ? 'reset' : 'login'
  );
  const [email, setEmail] = useState(initialEmail || '');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [whatsappConsent, setWhatsappConsent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // O tom vem do caller, não de adivinhar palavra dentro do texto. A versão
  // anterior olhava message.includes('Erro'), o que quebra em qualquer idioma.
  const [message, setMessage] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const loginLimiter = useRef(createRateLimiter(5, 60000));

  // Turnstile CAPTCHA
  const turnstileRef = useRef(null);
  // Timeout de segurança: o widget é invisível e, em WebView, pode nunca retornar
  // token nem erro — sem isto o spinner fica preso para sempre. O ref de "em
  // andamento" evita contar a mesma falha duas vezes (ex.: timeout + error).
  const captchaTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const captchaInFlightRef = useRef(false);
  const clearCaptchaTimeout = useCallback(() => {
    if (captchaTimeoutRef.current) {
      clearTimeout(captchaTimeoutRef.current);
      captchaTimeoutRef.current = null;
    }
  }, []);
  // Falha/expiração/timeout do CAPTCHA: registra no analytics (causa hoje
  // invisível) e destrava a UI com uma mensagem, em vez de spinner mudo.
  const handleCaptchaFailure = useCallback((code: string) => {
    if (!captchaInFlightRef.current) return;
    captchaInFlightRef.current = false;
    clearCaptchaTimeout();
    analytics.trackAuthError(code, pendingAction ?? mode);
    setIsSubmitting(false);
    setPendingAction(null);
    setMessage({ tone: 'error', text: t.errors.captcha_failed });
  }, [clearCaptchaTimeout, pendingAction, mode]);
  const { token: captchaToken, execute: executeTurnstile, reset: resetTurnstile, isReady: turnstileReady } = useTurnstile(turnstileRef, {
    onError: () => handleCaptchaFailure('captcha_error'),
    onExpired: () => handleCaptchaFailure('captcha_expired'),
  });

  // Executar ação de auth (compartilhada entre fluxo com e sem CAPTCHA)
  const executeAuthAction = useCallback(async (actionMode: string, token: string | null) => {
    try {
      if (actionMode === 'login') {
        const result = await login(email, password, token ?? undefined);
        if (result.success) {
          analytics.trackLogin('email');
          window.location.href = inviteToken ? `/project-invite/${inviteToken}` : redirectTo;
        } else {
          analytics.trackAuthError(result.errorCode ?? 'unknown', 'login');
          setMessage({ tone: 'error', text: t.errors.loginFailed });
        }
      } else if (actionMode === 'signup') {
        const result = await signUp(email, password, fullName, normalizePhoneBR(phone), whatsappConsent, token ?? undefined);
        if (result.success) {
          void analytics.trackSignUp('email', email);
          setMode('success');
        } else {
          analytics.trackAuthError(result.errorCode ?? 'unknown', 'signup');
          setMessage({ tone: 'error', text: t.errors[(result.errorCode ?? 'unknown') as keyof typeof t.errors] ?? t.errors.signupFailed });
        }
      } else if (actionMode === 'reset') {
        try {
          await resetPassword(email, token ?? undefined);
          setMessage({ tone: 'success', text: t.recoverySent });
        } catch (err) {
          setMessage({ tone: 'error', text: t.errors.resetFailed });
        }
      }
    } catch (err) {
      setMessage({ tone: 'error', text: t.errors.generic });
    } finally {
      captchaInFlightRef.current = false;
      clearCaptchaTimeout();
      setIsSubmitting(false);
      setPendingAction(null);
      if (turnstileReady) resetTurnstile();
    }
  }, [email, password, fullName, phone, whatsappConsent, login, signUp, resetPassword, redirectTo, turnstileReady, resetTurnstile, clearCaptchaTimeout]);

  // Quando o captchaToken chegar e houver ação pendente, executar (o CAPTCHA
  // resolveu, então cancela o timeout de falha e marca como concluído).
  useEffect(() => {
    if (!captchaToken || !pendingAction) return;
    captchaInFlightRef.current = false;
    clearCaptchaTimeout();
    executeAuthAction(pendingAction, captchaToken);
  }, [captchaToken, pendingAction, executeAuthAction, clearCaptchaTimeout]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (!loginLimiter.current.isAllowed()) {
      analytics.trackAuthError('rate_limit', mode);
      setMessage({ tone: 'error', text: t.errors.tooManyAttempts });
      return;
    }

    if (mode === 'signup') {
      if (!isValidPhoneBR(phone)) {
        setMessage({ tone: 'error', text: t.validation.invalidWhatsapp });
        return;
      }
      if (!acceptedTerms) {
        setMessage({ tone: 'error', text: t.validation.mustAcceptTerms });
        return;
      }
      const { valid, failures } = validatePassword(password);
      if (!valid) {
        setMessage({ tone: 'error', text: fill(copy.auth.validation.weakPassword, { failures: failures.map(ruleLabel).join(', ') }) });
        return;
      }
    }

    setIsSubmitting(true);

    if (turnstileReady) {
      // Fluxo com CAPTCHA: executar Turnstile, useEffect vai completar a ação.
      // Arma o timeout de falha (token nunca chega = falha silenciosa em WebView).
      setPendingAction(mode);
      captchaInFlightRef.current = true;
      clearCaptchaTimeout();
      captchaTimeoutRef.current = setTimeout(() => handleCaptchaFailure('captcha_timeout'), 15000);
      executeTurnstile();
    } else {
      // Sem CAPTCHA (dev ou não configurado): executar direto
      await executeAuthAction(mode, null);
    }
  };

  const handleOAuth = async (provider: string) => {
    setIsSubmitting(true);
    setMessage(null);
    // Mede o início do funil de OAuth. A "conclusão" (sign_up/login com
    // method=provider) é disparada no AuthContext.onAuthStateChange; a diferença
    // start-conclusão dimensiona o drop de OAuth (ex.: bloqueio em WebView).
    analytics.trackOAuthStart(provider);
    // Propagar redirectTo preserva fluxos como /oauth/consent?oauth_req_id=...
    // que de outro modo se perdem no callback do provedor.
    const result = await signInWithOAuth(provider, redirectTo);
    if (!result.success) {
      analytics.trackAuthError(result.errorCode ?? 'oauth_init_failed', 'oauth');
      setMessage({ tone: 'error', text: result.error || t.errors.oauthFailed });
      setIsSubmitting(false);
    }
  };

  const switchMode = (newMode: string) => {
    setMode(newMode);
    setMessage(null);
    setPassword('');
  };

  // Tela de sucesso após cadastro
  if (mode === 'success' || signUpSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-linear-to-br from-[#1a1a2e] via-[#2d1b69] to-[var(--color-primary)] p-5">
        <Card className="w-full max-w-[400px] shadow-[0_20px_60px_rgba(0,0,0,0.3)] text-center">
          <CardContent className="p-10">
            <div className="flex justify-center mb-6">
              <MailCheck size={64} className="text-[var(--color-success)]" />
            </div>
            <h2 className="text-[var(--font-2xl)] font-bold text-[var(--color-text)] m-0 mb-4">{t.signupSuccess.title}</h2>
            <p className="text-base text-[var(--color-gray-500)] m-0 mb-2">
              {t.signupSuccess.sentTo}
            </p>
            <p className="text-base font-semibold text-[var(--color-primary)] m-0 mb-4 break-all">{signUpEmail || email}</p>
            <p className="text-[var(--font-base)] text-[var(--color-gray-400)] m-0 mb-4 leading-normal">
              {t.signupSuccess.checkInbox}
            </p>
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm font-semibold text-indigo-700 m-0 mb-1.5">
                {t.signupSuccess.trialTitle}
              </p>
              <p className="text-xs text-indigo-600/80 m-0 leading-relaxed">
                {t.signupSuccess.trialBody}
              </p>
            </div>
            <Button
              variant="primary"
              size="xl"
              className="w-full"
              onClick={() => {
                clearSignUpSuccess();
                switchMode('login');
              }}
            >
              {t.switch.backToLogin}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-linear-to-br from-[#1a1a2e] via-[#2d1b69] to-[var(--color-primary)] p-5">
      <Card className="w-full max-w-[400px] shadow-[0_20px_60px_rgba(0,0,0,0.3)] [&_input:-webkit-autofill]:![box-shadow:0_0_0_1000px_#fff_inset] [&_input:-webkit-autofill]:![-webkit-text-fill-color:#1a1a2e] [&_input:-webkit-autofill:hover]:![box-shadow:0_0_0_1000px_#fff_inset] [&_input:-webkit-autofill:focus]:![box-shadow:0_0_0_1000px_#fff_inset]">
        <CardContent className="p-10">
        <div className="flex items-center justify-center gap-3 mb-8 [&_img]:w-12 [&_img]:h-12 [&_h1]:text-[var(--font-2xl)] [&_h1]:font-bold [&_h1]:text-[var(--color-primary)] [&_h1]:m-0">
          <img src="/logo.svg" alt="MeuGantt" />
          <h1>MeuGantt</h1>
        </div>

        <h2 className="text-center text-[var(--font-xl)] font-semibold text-[var(--color-gray-700)] m-0 mb-6">
          {mode === 'login' && t.title.login}
          {mode === 'signup' && t.title.signup}
          {mode === 'reset' && t.title.reset}
        </h2>

        <form className="flex flex-col gap-5" onSubmit={handleSubmit}>
          {mode === 'signup' && (
            <div className="flex flex-col gap-2 [&_label]:text-[var(--font-base)] [&_label]:font-medium [&_label]:text-[var(--color-gray-700)]">
              <Label htmlFor="fullName">{t.field.fullName}</Label>
              <div className="relative flex items-center [&_input]:w-full [&_input]:pl-11 [&_input]:pr-11 [&_input]:box-border">
                <User size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--color-gray-400)] pointer-events-none z-[1]" />
                <Input
                  id="fullName"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder={t.field.fullNamePlaceholder}
                  required
                  maxLength={INPUT_LIMITS.FULL_NAME}
                  className="py-3 px-4 pl-11 border-2 border-[var(--color-gray-200)] rounded-[var(--radius-lg)] text-base !bg-white !text-[#1a1a2e] transition-[border-color,box-shadow] duration-200 focus:outline-none focus:border-[var(--color-primary)] focus:shadow-[0_0_0_3px_rgba(126,62,238,0.15)]"
                />
              </div>
            </div>
          )}

          {mode === 'signup' && (
            <div className="flex flex-col gap-2 [&_label]:text-[var(--font-base)] [&_label]:font-medium [&_label]:text-[var(--color-gray-700)]">
              <Label htmlFor="phone">{t.field.whatsapp}</Label>
              <div className="relative flex items-center [&_input]:w-full [&_input]:pl-11 [&_input]:pr-11 [&_input]:box-border">
                <Phone size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--color-gray-400)] pointer-events-none z-[1]" />
                <Input
                  id="phone"
                  type="tel"
                  inputMode="numeric"
                  value={phone}
                  onChange={(e) => setPhone(formatPhoneBR(e.target.value))}
                  placeholder={t.field.whatsappPlaceholder}
                  required
                  className="py-3 px-4 pl-11 border-2 border-[var(--color-gray-200)] rounded-[var(--radius-lg)] text-base !bg-white !text-[#1a1a2e] transition-[border-color,box-shadow] duration-200 focus:outline-none focus:border-[var(--color-primary)] focus:shadow-[0_0_0_3px_rgba(126,62,238,0.15)]"
                />
              </div>
              <span className="text-xs text-[var(--color-gray-500)]">{t.field.whatsappHint}</span>
            </div>
          )}

          <div className="flex flex-col gap-2 [&_label]:text-[var(--font-base)] [&_label]:font-medium [&_label]:text-[var(--color-gray-700)]">
            <Label htmlFor="email">{t.field.email}</Label>
            <div className="relative flex items-center [&_input]:w-full [&_input]:pl-11 [&_input]:pr-11 [&_input]:box-border">
              <Mail size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--color-gray-400)] pointer-events-none z-[1]" />
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.field.emailPlaceholder}
                autoFocus
                required
                className="py-3 px-4 pl-11 border-2 border-[var(--color-gray-200)] rounded-[var(--radius-lg)] text-base !bg-white !text-[#1a1a2e] transition-[border-color,box-shadow] duration-200 focus:outline-none focus:border-[var(--color-primary)] focus:shadow-[0_0_0_3px_rgba(126,62,238,0.15)]"
              />
            </div>
          </div>

          {mode !== 'reset' && (
            <div className="flex flex-col gap-2 [&_label]:text-[var(--font-base)] [&_label]:font-medium [&_label]:text-[var(--color-gray-700)]">
              <Label htmlFor="password">{t.field.password}</Label>
              <div className="relative flex items-center [&_input]:w-full [&_input]:pl-11 [&_input]:pr-11 [&_input]:box-border">
                <Lock size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--color-gray-400)] pointer-events-none z-[1]" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t.field.passwordPlaceholder}
                  required
                  minLength={mode === 'signup' ? PASSWORD_MIN_LENGTH : undefined}
                  className="w-full pr-12 box-border py-3 px-4 pl-11 border-2 border-[var(--color-gray-200)] rounded-[var(--radius-lg)] text-base !bg-white !text-[#1a1a2e] transition-[border-color,box-shadow] duration-200 focus:outline-none focus:border-[var(--color-primary)] focus:shadow-[0_0_0_3px_rgba(126,62,238,0.15)]"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 bg-none border-none text-[var(--color-gray-500)] cursor-pointer p-1 flex items-center justify-center hover:text-[var(--color-gray-700)]"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {mode === 'signup' && (
                <div className="flex flex-col gap-0.5 mt-1.5">
                  {PASSWORD_RULES.map((rule) => {
                    const passed = password && rule.test(password);
                    return (
                      <span key={rule.key} className={cn("text-xs transition-colors duration-200", passed ? "text-[var(--color-success)]" : "text-[var(--color-gray-500)]")}>
                        {passed ? '✓' : '○'} {ruleLabel(rule.key)}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {mode === 'signup' && (
            <div className="-mt-2">
              <label className="flex items-start gap-2 text-[13px] text-[var(--color-gray-500)] cursor-pointer leading-snug [&_input[type=checkbox]]:mt-0.5 [&_input[type=checkbox]]:accent-[var(--color-primary)] [&_input[type=checkbox]]:shrink-0 [&_a]:text-[var(--color-primary)] [&_a]:no-underline [&_a:hover]:underline">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                />
                <span>
                  {t.terms.acceptLead}{' '}
                  <a href="/termos" target="_blank" rel="noopener noreferrer">{t.terms.termsLink}</a>
                  {' '}{t.terms.and}{' '}
                  <a href="/privacidade" target="_blank" rel="noopener noreferrer">{t.terms.privacyLink}</a>
                </span>
              </label>
            </div>
          )}

          {mode === 'signup' && (
            <div className="-mt-3">
              <label className="flex items-start gap-2 text-[13px] text-[var(--color-gray-500)] cursor-pointer leading-snug [&_input[type=checkbox]]:mt-0.5 [&_input[type=checkbox]]:accent-[var(--color-primary)] [&_input[type=checkbox]]:shrink-0">
                <input
                  type="checkbox"
                  checked={whatsappConsent}
                  onChange={(e) => setWhatsappConsent(e.target.checked)}
                />
                <span>{t.terms.whatsappConsent}</span>
              </label>
            </div>
          )}

          {message && (
            <div className={cn("py-3 px-4 rounded-[var(--radius-lg)] text-[var(--font-base)] text-center", message.tone === 'error' ? "bg-[var(--color-danger-bg)] text-[var(--color-danger-dark)] border border-[var(--color-danger-border)]" : "bg-[var(--color-success-bg)] text-[var(--color-success-text)] border border-[var(--color-success-border)]")}>
              {message.text}
            </div>
          )}

          {error && !message && (
            <div className="bg-[var(--color-danger-bg)] text-[var(--color-danger-dark)] py-3 px-4 rounded-[var(--radius-lg)] text-[var(--font-base)] text-center border border-[var(--color-danger-border)]">{error}</div>
          )}

          {/* Container invisível para o Turnstile CAPTCHA */}
          <div ref={turnstileRef} />

          <Button type="submit" variant="primary" size="xl" className="w-full" disabled={isSubmitting || loading}>
            {(isSubmitting || loading) ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                {mode === 'login' && t.loading.login}
                {mode === 'signup' && t.loading.signup}
                {mode === 'reset' && t.loading.sending}
              </>
            ) : (
              <>
                {mode === 'login' && t.action.login}
                {mode === 'signup' && t.action.signup}
                {mode === 'reset' && t.action.sendEmail}
              </>
            )}
          </Button>
        </form>

        {/* Botão OAuth Google — só no login/signup, nunca no admin */}
        {(mode === 'login' || mode === 'signup') && !isAdminLogin && (
          <div className="flex flex-col gap-3 mt-5">
            <div className="flex items-center gap-4 mt-1 before:content-[''] before:flex-1 before:h-px before:bg-[var(--color-gray-200)] after:content-[''] after:flex-1 after:h-px after:bg-[var(--color-gray-200)]">
              <span className="text-[var(--font-sm)] text-[var(--color-gray-400)] lowercase">{t.switch.or}</span>
            </div>
            <Button
              type="button"
              variant="outline"
              size="xl"
              className="w-full border-2 border-[var(--color-gray-200)] text-[var(--color-gray-700)] hover:border-[var(--color-gray-300)] hover:bg-[var(--color-gray-50)] hover:shadow-[0_2px_8px_rgba(0,0,0,0.06)]"
              onClick={() => handleOAuth('google')}
              disabled={isSubmitting || loading}
            >
              <GoogleIcon />
              <span>{t.action.google}</span>
            </Button>
          </div>
        )}

        <div className="flex flex-col items-center gap-3 mt-6 pt-6 border-t border-[var(--color-gray-200)]">
          {mode === 'login' && (
            <>
              {!isAdminLogin && (
                <Button type="button" variant="link" className="text-[var(--color-gray-500)] hover:text-[var(--color-primary)]" onClick={() => switchMode('signup')}>
                  {t.switch.noAccount} <strong className="text-[var(--color-primary)] font-semibold">{t.switch.signupCta}</strong>
                </Button>
              )}
              <Button type="button" variant="link" className="text-[var(--color-gray-500)] hover:text-[var(--color-primary)]" onClick={() => switchMode('reset')}>
                {t.switch.forgotPassword}
              </Button>
            </>
          )}
          {mode !== 'login' && (
            <Button type="button" variant="link" className="text-[var(--color-gray-500)] hover:text-[var(--color-primary)]" onClick={() => switchMode('login')}>
              {t.switch.backToLogin}
            </Button>
          )}
        </div>

        <div className="text-center mt-5 text-xs text-[var(--color-gray-400)] [&_a]:text-[var(--color-gray-400)] [&_a]:no-underline [&_a:hover]:text-[var(--color-primary)] [&_a:hover]:underline">
          <a href="/privacidade" target="_blank" rel="noopener noreferrer">
            {t.terms.privacyLink}
          </a>
          <span className="mx-2">|</span>
          <a href="/termos" target="_blank" rel="noopener noreferrer">
            {t.terms.termsLink}
          </a>
        </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginScreen;
